import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { queryClient } from '../lib/query/index.js';
import { impersonationStorage } from '../lib/impersonation-bootstrap.js';
import { storageKey } from '../lib/storage-key.js';

function updateSentryUser(subject: { id: string; agentId: string } | null) {
  // Lazy import to avoid coupling tests to Sentry
  import('../lib/sentry/index.js').then(({ setSentryUser }) => setSentryUser(subject)).catch(() => {});
}

export type Subject = {
  id: string;
  kind: 'player';
  agentId: string;
  currency: string;
  displayName?: string;
};

type SetAuthArgs = {
  accessToken: string;
  /**
   * `undefined` keeps whatever is stored; `null` clears it. Callers pass what
   * the server actually returned (`res.refreshToken ?? null`): in cookie mode
   * the body carries no token, and keeping the previous one would replay an
   * already-rotated token on the next refresh and trip the theft detector.
   */
  refreshToken?: string | null;
  subject?: Subject;
};

type AuthState = {
  accessToken: string | null;
  refreshToken: string | null;
  subject: Subject | null;
  setAuth: (args: SetAuthArgs) => void;
  setSubject: (subject: Subject) => void;
  clear: () => void;
};

export const useAuthStore = create<AuthState>()(
  persist(
    (set) => ({
      accessToken: null,
      refreshToken: null,
      subject: null,
      setAuth: ({ accessToken, refreshToken, subject }) => {
        set((prev) => ({
          accessToken,
          refreshToken: refreshToken === undefined ? prev.refreshToken : refreshToken,
          subject: subject ?? prev.subject,
        }));
        // subject may come later via setSubject; update if available now
        if (subject) updateSentryUser(subject);
      },
      setSubject: (subject) => {
        set({ subject });
        updateSentryUser(subject);
      },
      clear: () => {
        set({ accessToken: null, refreshToken: null, subject: null });
        updateSentryUser(null);
        // Clear the QueryClient cache SYNCHRONOUSLY (same tick as the auth reset) so the
        // logout re-render never shows the previous member's wallet/username. A deferred
        // (dynamic-import) clear left stale data visible until the chunk resolved.
        queryClient.clear();
        // Drop the Chatwoot contact session so the next login doesn't inherit
        // the previous member's conversation. Lazy import keeps tests decoupled.
        import('../lib/chatwoot/index.js').then(({ resetChatwoot }) => resetChatwoot()).catch(() => {});
      },
    }),
    {
      name: storageKey('auth'),
      // Impersonation tabs use sessionStorage (per-tab, flag set by
      // impersonation-bootstrap before this store initializes) so a staff
      // "view as member" session never clobbers a real member's localStorage
      // session, which is shared across all tabs of this origin.
      storage: createJSONStorage(() => impersonationStorage()),
      partialize: (s) => ({
        accessToken: s.accessToken,
        refreshToken: s.refreshToken,
        subject: s.subject,
      }),
    },
  ),
);
